import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess, TrendingUp, TrendingDown } from '@mui/icons-material';

const DailySalesTable = ({ monthlyData, title = "Relatório Detalhado de Vendas Diárias" }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedMonth, setExpandedMonth] = useState(null);

  // Flatten all daily data from monthly breakdown
  const allDailyData = [];
  if (monthlyData && monthlyData.length > 0) {
    monthlyData.forEach(month => {
      if (month.daily_breakdown && month.daily_breakdown.length > 0) {
        month.daily_breakdown.forEach(day => {
          allDailyData.push({
            ...day,
            month_name: month.month_name,
            year: month.year,
          });
        });
      }
    });
  }

  // Usar apenas dados reais do backend
  const dataToUse = allDailyData.length > 0 ? allDailyData : [];
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDayChipColor = (dayName) => {
    if (dayName === 'Sáb' || dayName === 'Dom') return 'secondary';
    return 'primary';
  };

  const getTrendIcon = (revenue, baseline) => {
    // Tendência relativa: compara receita do dia com média diária do período
    // Desvio > +10%: alta; < -10%: baixa; caso contrário, neutro
    if (!baseline || baseline <= 0) return null;
    const diff = (revenue - baseline) / baseline;
    if (diff >= 0.1) return <TrendingUp color="success" fontSize="small" />;
    if (diff <= -0.1) return <TrendingDown color="error" fontSize="small" />;
    return null;
  };

  // Calculate summary statistics
  const totalDays = dataToUse.length;
  const totalSales = dataToUse.reduce((sum, day) => sum + (day.sales || day.total_sales || 0), 0);
  const totalRevenue = dataToUse.reduce((sum, day) => sum + (parseFloat(day.revenue || day.total_revenue || 0)), 0);
  const avgDailySales = totalSales / totalDays;
  const avgDailyRevenue = totalRevenue / totalDays;
  const avgTicket = totalRevenue / totalSales;

  const paginatedData = dataToUse.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!dataToUse || dataToUse.length === 0) {
    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography color="text.secondary">
              Não há dados disponíveis para exibir
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            {title}
          </Typography>

          {/* Summary Statistics */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Estatísticas Gerais do Período Analisado
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Total de dias analisados:</Typography>
                <Typography variant="h6" color="primary">{totalDays} dias</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total de vendas no período:</Typography>
                <Typography variant="h6" color="success.main">{totalSales.toLocaleString('pt-BR')} transações</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Receita total do período:</Typography>
                <Typography variant="h6" color="success.main">
                  R$ {totalRevenue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Média diária de vendas:</Typography>
                <Typography variant="h6">{avgDailySales.toLocaleString('pt-BR', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })} transações</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Média diária de receita:</Typography>
                <Typography variant="h6">
                  R$ {avgDailyRevenue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Ticket médio geral:</Typography>
                <Typography variant="h6">
                  R$ {avgTicket.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell align="center"><strong>Vendas</strong></TableCell>
                  <TableCell align="right"><strong>Receita (R$)</strong></TableCell>
                  <TableCell align="right"><strong>Ticket Médio (R$)</strong></TableCell>
                  <TableCell align="center"><strong>Estados</strong></TableCell>
                  <TableCell align="center"><strong>Dia</strong></TableCell>
                  <TableCell align="center"><strong>Tendência</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => {
                  const revenue = parseFloat(row.revenue || row.total_revenue || 0);
                  const sales = row.sales || row.total_sales || 0;
                  const avgTicketRow = row.avg_ticket || row.average_ticket || (revenue / sales);
                  const dateStr = row.date || (row.date ? row.date.toLocaleDateString('pt-BR') : `${index + 1}`);
                  
                  return (
                    <TableRow key={index} hover>
                      <TableCell component="th" scope="row">
                        <strong>{dateStr}</strong>
                      </TableCell>
                      <TableCell align="center">
                        {sales.toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell align="right">
                        R$ {revenue.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </TableCell>
                      <TableCell align="right">
                        R$ {parseFloat(avgTicketRow).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {row.states || 27}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={row.day_name || row.day_of_week || 'N/A'} 
                          size="small" 
                          color={getDayChipColor(row.day_name || row.day_of_week)} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        {getTrendIcon(revenue, avgDailyRevenue)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={dataToUse.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default DailySalesTable;
